/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * EditorPageCoverPicture components using @atlaskit/css
 * This file contains the new CSS implementation for the migration
 */
import { type FC, useMemo } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE } from '../pageCoverPictureConstants.ts';

const COVER_IMAGE_FIXED_HEIGHT = 147;
const SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH = 200;

import type { CoverPictureWrapperProps } from './EditorPageCoverPictureWrapper.tsx';

const styles = cssMap({
	base: {
		position: 'relative',
		marginBottom: token('space.100'),
	},
	noMargin: {
		marginTop: token('space.0'),
		marginBottom: token('space.0'),
	},
	pointer: { cursor: 'pointer' },
	auto: { cursor: 'auto' },
	withPicture: { marginTop: token('space.075') },
	withoutPicture: { marginTop: token('space.0') },
	hidden: { height: '0px' },
	fixedWidth: { height: `${COVER_IMAGE_FIXED_HEIGHT}px` },
	smartSpaceOverviewHeight: { height: `${SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH}px` },
	scaledWidth: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- CSS calc expression for responsive height
		height: CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE as `${number}px`,
	},
});

export const CoverPictureWrapperCss: FC<CoverPictureWrapperProps> = ({
	hasCoverPicture,
	isUploadingPicture,
	isReadOnly,
	isFixedWidthImageOption,
	isSmartSpaceOverview,
	width,
	children,
	...props
}) => {
	'use no memo'; // Exclude from React compiler - breaks unit tests when component uses compiled CSS

	// Dynamic width - must use style prop
	const computedWidth = useMemo(() => {
		if ((hasCoverPicture || isUploadingPicture) && isFixedWidthImageOption) {
			return '800px';
		}
		if ((hasCoverPicture || isUploadingPicture) && !isFixedWidthImageOption) {
			return '100%';
		}
		if ((hasCoverPicture || isUploadingPicture) && width) {
			return `${width}px`;
		}
		return '100%';
	}, [hasCoverPicture, isUploadingPicture, isFixedWidthImageOption, width]);

	const hasPicture = hasCoverPicture || isUploadingPicture;
	const showPictureMargin = !isSmartSpaceOverview && hasPicture;
	const showNoPictureMargin = !isSmartSpaceOverview && !hasPicture;

	return (
		<div
			{...props}
			style={{ width: computedWidth }}
			css={[
				styles.base,
				isSmartSpaceOverview && styles.noMargin,
				isReadOnly ? styles.auto : styles.pointer,
				showPictureMargin && styles.withPicture,
				showNoPictureMargin && styles.withoutPicture,
				!hasPicture
					? styles.hidden
					: isSmartSpaceOverview
						? styles.smartSpaceOverviewHeight
						: isFixedWidthImageOption
							? styles.fixedWidth
							: styles.scaledWidth,
			]}
		>
			{children}
		</div>
	);
};
